import { useState, useCallback } from 'react';

const STATUS_STYLES = {
  delivered: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  shipped: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  in_transit: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  production: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  codes_completed: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  canceled: 'bg-red-50 text-red-600 ring-red-600/20',
};

const ALL_STATUSES = ['production', 'codes_completed', 'shipped', 'in_transit', 'delivered', 'canceled'];
const DEFAULT_STATUS_STYLE = 'bg-gray-50 text-gray-600 ring-gray-500/20';

function carrierTrackingUrl(carrier, tracking) {
  if (!tracking) return null;
  const c = (carrier || '').toUpperCase();
  if (c === 'FEDEX') return `https://www.fedex.com/fedextrack/?trknbr=${tracking}`;
  if (c === 'UPS') return `https://www.ups.com/track?tracknum=${tracking}`;
  return null;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Ship-to address as display lines: who (company or contact), street, city/state/zip.
// Backbook orders often have no address yet, so every part is optional.
export function shipToLines(order) {
  const clean = (v) => (typeof v === 'string' ? v.trim() : v) || '';
  const company = clean(order.recipient_company);
  const contact = clean(order.recipient_name);
  const who = company || contact;
  const attn = company && contact && contact !== company ? contact : '';
  const street = [order.recipient_street1, order.recipient_street2, order.recipient_street3]
    .map(clean).filter(Boolean).join(', ');
  const stateZip = [clean(order.recipient_state), clean(order.recipient_zip)].filter(Boolean).join(' ');
  const cityLine = [clean(order.recipient_city), stateZip].filter(Boolean).join(', ');
  const address = [street, cityLine].filter(Boolean).join(', ');
  const full = [who, attn && `Attn: ${attn}`, street, cityLine].filter(Boolean).join('\n');
  return { who, address, full };
}

function toInputDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().slice(0, 10);
}

export default function OrdersTable({ orders, flaggedIds, onOrderUpdated, secretKey, onNeedAuth, onAuthFailed }) {
  const [sortKey, setSortKey] = useState('greenlight_date');
  const [sortDir, setSortDir] = useState('desc');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleEditClick = useCallback((order) => {
    if (secretKey) {
      setEditingId(order.id);
      setEditValues({
        status: order.status || '',
        shipped_at: toInputDate(order.shipped_at),
        delivered_at: toInputDate(order.delivered_at),
        notes: order.notes || '',
        originalNotes: order.notes || '',
      });
      setError(null);
    } else {
      onNeedAuth(order.id);
    }
  }, [secretKey, onNeedAuth]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/orders/${editingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret: secretKey,
          status: editValues.status,
          shipped_at: editValues.shipped_at || null,
          delivered_at: editValues.delivered_at || null,
          // Only sent when changed, so status/date edits keep working before migration 014 runs
          ...(editValues.notes !== editValues.originalNotes && { notes: editValues.notes }),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          onAuthFailed();
          setEditingId(null);
          return;
        }
        throw new Error(data.error || 'Save failed');
      }
      setEditingId(null);
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [editingId, editValues, secretKey, onOrderUpdated, onAuthFailed]);

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
    setError(null);
  };

  const sortValue = (o) => (sortKey === 'ship_to' ? shipToLines(o).full || null : o[sortKey]);

  const sorted = [...orders].sort((a, b) => {
    let aVal = sortValue(a);
    let bVal = sortValue(b);
    if (typeof aVal === 'boolean' || typeof bVal === 'boolean') { aVal = aVal ? 1 : 0; bVal = bVal ? 1 : 0; }
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const columns = [
    { key: 'order_uid', label: 'Order' },
    { key: 'client', label: 'Client' },
    { key: 'batch_production', label: 'Batch' },
    { key: 'send_authorized', label: 'Authorized' },
    { key: 'tracking', label: 'Tracking' },
    { key: 'status', label: 'Status' },
    { key: 'ship_carrier', label: 'Carrier' },
    { key: 'greenlight_date', label: 'Greenlight' },
    { key: 'shipped_at', label: 'Shipped' },
    { key: 'delivered_at', label: 'Delivered' },
    { key: 'ship_to', label: 'Ship To' },
    { key: 'notes', label: 'Notes' },
    { key: '_actions', label: '' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={col.key !== '_actions' ? () => handleSort(col.key) : undefined}
                className={`text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider select-none whitespace-nowrap transition-colors ${
                  col.key === '_actions'
                    ? 'sticky right-0 bg-gray-50 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]'
                    : 'cursor-pointer hover:text-gray-700'
                }`}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {sortKey === col.key && (
                    <svg className={`w-3 h-3 transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((order, idx) => {
            const isFlagged = flaggedIds.has(order.id);
            const url = carrierTrackingUrl(order.ship_carrier, order.tracking);
            const statusStyle = STATUS_STYLES[order.status] || DEFAULT_STATUS_STYLE;
            const isLast = idx === sorted.length - 1;
            const isEditing = editingId === order.id;
            const shipTo = shipToLines(order);

            return (
              <tr
                key={order.id}
                className={`${
                  isEditing ? 'bg-indigo-50/60' : isFlagged ? 'bg-amber-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                } ${!isLast ? 'border-b border-gray-100/70' : ''} hover:bg-indigo-50/40 transition-colors`}
              >
                <td className="px-5 py-3 whitespace-nowrap">
                  <span className="font-semibold text-gray-900 font-mono text-xs">{order.order_uid}</span>
                  {isFlagged && <span className="ml-1.5 inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" />}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-gray-600">{order.client || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-gray-600 text-xs">{order.batch_production || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {order.send_authorized ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Yes
                    </span>
                  ) : (
                    <span className="text-gray-300 text-xs">No</span>
                  )}
                </td>
                <td className="px-5 py-3 whitespace-nowrap font-mono text-xs">
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors">
                      {order.tracking}
                    </a>
                  ) : (
                    <span className="text-gray-300">{order.tracking || '—'}</span>
                  )}
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {isEditing ? (
                    <select
                      value={editValues.status}
                      onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                      className="px-2 py-1 bg-white border border-indigo-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    >
                      {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${statusStyle}`}>
                      {order.status || '—'}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-gray-500 text-xs uppercase tracking-wide">{order.ship_carrier || '—'}</td>
                <td className="px-5 py-3 whitespace-nowrap text-gray-500">{formatDate(order.greenlight_date)}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {isEditing ? (
                    <input type="date" value={editValues.shipped_at} onChange={(e) => setEditValues({ ...editValues, shipped_at: e.target.value })}
                      className="px-2 py-1 bg-white border border-indigo-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20" />
                  ) : (
                    <span className="text-gray-500">{formatDate(order.shipped_at)}</span>
                  )}
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {isEditing ? (
                    <input type="date" value={editValues.delivered_at} onChange={(e) => setEditValues({ ...editValues, delivered_at: e.target.value })}
                      className="px-2 py-1 bg-white border border-indigo-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20" />
                  ) : (
                    <span className="text-gray-500">{formatDate(order.delivered_at)}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-xs">
                  {shipTo.full ? (
                    <div className="max-w-[20rem]" title={shipTo.full}>
                      {shipTo.who && <span className="block truncate text-gray-700">{shipTo.who}</span>}
                      {shipTo.address && <span className="block truncate text-gray-400">{shipTo.address}</span>}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-5 py-3 text-xs">
                  {isEditing ? (
                    <input type="text" value={editValues.notes} placeholder="Add a note"
                      onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                      className="w-56 px-2 py-1 bg-white border border-indigo-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20" />
                  ) : order.notes ? (
                    <span className="block max-w-[14rem] truncate text-gray-600" title={order.notes}>{order.notes}</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className={`px-5 py-3 whitespace-nowrap sticky right-0 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] ${
                  isEditing ? 'bg-indigo-50' : isFlagged ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                }`}>
                  {isEditing ? (
                    <div className="flex items-center gap-1.5">
                      <button onClick={handleSave} disabled={saving}
                        className="px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={handleCancel} className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors">Cancel</button>
                      {error && <span className="text-xs text-red-500">{error}</span>}
                    </div>
                  ) : (
                    <button onClick={() => handleEditClick(order)} className="text-gray-300 hover:text-indigo-600 transition-colors p-1" title="Edit order">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr><td colSpan={columns.length}className="px-5 py-16 text-center"><p className="text-gray-400 text-sm">No orders match your filters.</p></td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
